import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  loadReportTemplates,
  saveReportTemplate,
  deleteReportTemplate,
} from "../../Redux/Actions/ActionStatistics";
import "./ReportTemplates.css";

const STORAGE_KEY_PREFIX = "miamed_tpl_collapsed_";

/**
 * FE-11 — Report Templates (collapsible)
 * Save / load / delete filter presets per report.
 *
 * Props:
 *   reportKey  — current report key (e.g. "visits_frequeny_report")
 *   filters    — current filter state object to save
 *   onApply    — callback(filters) when user loads a template
 *   translate  — i18n function
 */
function ReportTemplates({ reportKey, filters, onApply, translate }) {
  const dispatch = useDispatch();
  const templates = useSelector((s) => s.statisticss.reportTemplates) || [];

  const [name, setName] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [collapsed, setCollapsed] = useState(() => {
    try { return localStorage.getItem(STORAGE_KEY_PREFIX + reportKey) === "1"; } catch { return false; }
  });

  useEffect(() => {
    if (reportKey) dispatch(loadReportTemplates(reportKey));
  }, [reportKey, dispatch]);

  // Reset collapsed state when report changes
  useEffect(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY_PREFIX + reportKey);
      setCollapsed(saved === "1");
    } catch { setCollapsed(false); }
  }, [reportKey]);

  const handleSave = () => {
    if (!name.trim()) return;
    dispatch(
      saveReportTemplate(
        { report_key: reportKey, name: name.trim(), filters: filters || {} },
        () => {
          setName("");
          setShowForm(false);
        }
      )
    );
  };

  const handleDelete = (id) => {
    dispatch(deleteReportTemplate(id, reportKey));
  };

  const handleLoad = (tpl) => {
    if (onApply) onApply(tpl.filters);
  };

  const toggleCollapse = () => {
    const next = !collapsed;
    setCollapsed(next);
    try { localStorage.setItem(STORAGE_KEY_PREFIX + reportKey, next ? "1" : "0"); } catch {}
  };

  return (
    <div className={`report-templates${collapsed ? " collapsed" : ""}`}>
      <div className="report-templates-header" onClick={toggleCollapse}>
        <span className="las la-bookmark"></span>
        <span>{translate("templates_title")}</span>
        <span className={`las ${collapsed ? "la-angle-down" : "la-angle-up"} tpl-collapse-icon`}></span>
        <button
          className="tpl-toggle-btn"
          onClick={(e) => { e.stopPropagation(); setShowForm((p) => !p); }}
          title={translate("templates_save_new")}
        >
          <span className={showForm ? "las la-minus" : "las la-plus"}></span>
        </button>
      </div>

      {!collapsed && (
        <>
          {/* Save form */}
          {showForm && (
            <div className="tpl-save-form">
              <input
                type="text"
                className="tpl-input"
                placeholder={translate("templates_name_placeholder")}
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
              <button className="tpl-btn tpl-btn-save" onClick={handleSave}>
                {translate("templates_save")}
              </button>
            </div>
          )}

          {/* Template list */}
          <div className="tpl-list">
            {templates.length === 0 && (
              <div className="tpl-empty">{translate("templates_empty")}</div>
            )}
            {templates.map((tpl) => (
              <div key={tpl.id} className="tpl-item">
                <div className="tpl-item-info">
                  <span className="tpl-item-name">{tpl.name}</span>
                  {tpl.is_default && (
                    <span className="tpl-item-badge">
                      {translate("templates_default")}
                    </span>
                  )}
                </div>
                <div className="tpl-item-actions">
                  <button
                    className="tpl-btn tpl-btn-load"
                    onClick={() => handleLoad(tpl)}
                    title={translate("templates_apply")}
                  >
                    <span className="las la-download"></span>
                  </button>
                  <button
                    className="tpl-btn tpl-btn-delete"
                    onClick={() => handleDelete(tpl.id)}
                    title={translate("templates_delete")}
                  >
                    <span className="las la-trash"></span>
                  </button>
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

export default ReportTemplates;
